import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { BreadcrumbsV2, DynamicMediaItem } from '@uncinc/react-drupal-core';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { Container } from '@uncinc/uncinc-react-kitchen-sink';

import { taxonomyTagEntityMapper } from '../../../../components/Tags/taxonomyTagMapper';
import fundersWidgetMapper from '../../../../components/Widgets/FundersWidget/fundersWidgetMapper';
import nodePartnersWidgetMapper from '../../../../components/Widgets/PartnersWidget/nodePartnersWidgetMapper';
import profileTeamWidgetMapper from '../../../../components/Widgets/TeamWidget/profileTeamWidgetMapper';
import type { NodeProject } from '../../../../types/uncinc_profile/nodes';
import {
  DateTime,
  FundersWidget,
  ParagraphsRenderer,
  PartnersWidget,
  RelatedContent,
  SearchMoreLikeThis,
  Tags,
  TeamWidget,
} from '../../../../components';

import './style.scss';
import { RELATED_ARTICLES_HITS_PER_PAGE } from '../../../../config/constants';

interface NodeProjectPageProps {
  entity: ImmutableEntity<NodeProject>;
  className?: string;
}

const NodeProjectPage: React.FC<NodeProjectPageProps> = ({ entity, className }) => {
  const { t } = useTranslation();

  const project = entity.toJS();
  const nid = project.nid[0].value;
  const langcode = project.langcode[0].value;
  const title = project.title[0].value;
  const departments = project.field_departments;
  const themes = project.field_themes;
  const duration = project.field_duration[0];
  const durationStartDate = duration?.value;
  const durationEndDate = duration?.end_value;
  const media = project.field_media_item[0];
  const paragraphs = project.field_paragraphs;
  const researchLeaders = project.field_research_leaders;
  const projectTeam = project.field_project_team;
  const funders = project.field_funders;
  const partners = project.field_partner;

  const classes = classNames('Node', 'NodeProjectPage', className);
  const sidebarColClasses = classNames({
    'col-12 col-md-5 col-lg-4 offset-lg-1': paragraphs.length > 0,
    'col-12 col-md-7': paragraphs.length === 0,
  });

  return (
    <article itemType="http://schema.org/WebPage" className={classes}>
      <div className="NodeProjectPage__hero">
        <Container>
          <div className="row">
            <div className="col-12">
              <BreadcrumbsV2 showHomeIcon={false} />
            </div>
          </div>

          <div className="row">
            <div className="col-12 col-md-10 col-lg-9">
              <div className="NodeProjectPage__hero__text-wrapper">
                <div className="NodeProjectPage__hero__text">
                  <h1 className="NodeProjectPage__title" itemProp="title">
                    {title}
                  </h1>

                  {(departments.length > 0 || themes.length > 0) && (
                    <div className="NodeProjectPage__tags">
                      {departments.length > 0 && (
                        <Tags
                          title={t('taxonomies.departments.title', { count: departments.length })}
                          items={taxonomyTagEntityMapper(departments)}
                        />
                      )}
                      {themes.length > 0 && (
                        <Tags
                          title={t('taxonomies.themes.title', { count: themes.length })}
                          items={taxonomyTagEntityMapper(themes)}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {(durationStartDate && durationEndDate) && (
              <div className="col-12 col-lg-3">
                <div className="NodeProjectPage__hero__duration-wrapper">
                  <div className="NodeProjectPage__hero__duration">
                    <DateTime
                      title={t('content_types.project.duration.title')}
                      date={new Date(durationStartDate)}
                      endDate={new Date(durationEndDate)}
                      translationKey="content_types.project.duration.datetime"
                      langcode={langcode as unknown as 'nl' | 'en'}
                    />
                  </div>
                </div>
              </div>
            )}

            {media && (
              <div className="col-12">
                <div className="NodeProjectPage__hero__media-wrapper">
                  <div className="NodeProjectPage__hero__media">
                    <DynamicMediaItem
                      media={media}
                      imageAspectRatio="16_9"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </Container>
      </div>

      <Container>
        <div className="row">
          {paragraphs.length > 0 && (
            <div className="col-12 col-md-7">
              <ParagraphsRenderer
                className="NodeProjectPage__paragraphs"
                paragraphs={paragraphs}
                noBootstrap
              />
            </div>
          )}

          <div className={sidebarColClasses}>
            <aside className="NodeProjectPage__sidebar">
              {(researchLeaders.length > 0 || projectTeam.length > 0) && (
                <TeamWidget
                  leads={profileTeamWidgetMapper(researchLeaders)}
                  members={profileTeamWidgetMapper(projectTeam)}
                />
              )}

              <FundersWidget
                funders={fundersWidgetMapper(funders)}
              />

              {(partners && partners.length > 0) && (
                <PartnersWidget
                  partners={nodePartnersWidgetMapper(partners)}
                />
              )}
            </aside>
          </div>
        </div>
      </Container>

      <div className="FixedReferenceBlocks">
        <Container>
          <div className="row">
            <div className="col-12">
              <div id="articles" className="TaxonomyDepartment__articles">
                <RelatedContent
                  title={t('content_types.article.related_content')}
                  contentType="article"
                  hitsPerPage={RELATED_ARTICLES_HITS_PER_PAGE}
                  filterName="projects_nid"
                  filterValue={nid}
                  langcode={langcode}
                />
              </div>
            </div>
          </div>
        </Container>

        <div className="NodeProjectPage__related-content">
          <Container>
            <div className="row">
              <div className="col-12 col-xl-10 offset-xl-1">
                <SearchMoreLikeThis
                  entityId={nid}
                  langcode={langcode}
                  contentTypes={['project']}
                  title={t('content_types.project.related_content')}
                />
              </div>
            </div>
          </Container>
        </div>
      </div>
    </article>
  );
};

export default NodeProjectPage;
